import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Play, Pause, Check, Music } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Searchable built-in music library (tracks are original works generated for
// this app — licensed for any use). Mood/genre filters + audition playback.
export default function MusicLibrary({ selectedTrackId, onSelect }) {
  const [tracks, setTracks] = useState([]);
  const [moods, setMoods] = useState([]);
  const [genres, setGenres] = useState([]);
  const [search, setSearch] = useState("");
  const [mood, setMood] = useState("");
  const [genre, setGenre] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/funtime/music-library`).then((res) => {
      setTracks(res.data?.tracks || []);
      setMoods(res.data?.moods || []);
      setGenres(res.data?.genres || []);
    }).catch(() => {});
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  const audition = (track) => {
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(`${API}/funtime/music-library/${track.id}/audio`);
    a.crossOrigin = "use-credentials";
    a.onended = () => setPlayingId(null);
    a.play().catch(() => {});
    audioRef.current = a;
    setPlayingId(track.id);
  };

  const visible = tracks.filter((t) =>
    (!search || t.title.toLowerCase().includes(search.toLowerCase())) &&
    (!mood || t.mood === mood) && (!genre || t.genre === genre)
  );
  const selCls = "h-8 px-2 rounded-md border border-border bg-background font-mono text-xs";

  return (
    <div className="space-y-2" data-testid="music-library">
      <div className="flex flex-wrap gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tracks…" className="h-8 w-40 font-mono text-xs" data-testid="ml-search" />
        <select value={mood} onChange={(e) => setMood(e.target.value)} className={selCls} data-testid="ml-mood">
          <option value="">All moods</option>
          {moods.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={genre} onChange={(e) => setGenre(e.target.value)} className={selCls} data-testid="ml-genre">
          <option value="">All genres</option>
          {genres.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
        {visible.length === 0 && (
          <p className="font-mono text-xs text-muted-foreground py-4 col-span-2 text-center">No tracks match your filters.</p>
        )}
        {visible.map((t, i) => (
          <div key={t.id} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${selectedTrackId === t.id ? "border-primary bg-primary/10" : "border-border/60 bg-secondary/30"}`} data-testid={`ml-track-${i}`}>
            <button onClick={() => audition(t)} className="p-1.5 rounded-full bg-primary/20 text-primary hover:bg-primary/30" title="Listen" data-testid={`ml-play-${i}`}>
              {playingId === t.id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs font-bold text-card-foreground truncate flex items-center gap-1"><Music className="w-3 h-3 text-primary" />{t.title}</p>
              <p className="font-mono text-[10px] text-muted-foreground">{t.mood} · {t.genre} · {Math.round(t.duration)}s · royalty-free</p>
            </div>
            <Button size="sm" variant={selectedTrackId === t.id ? "default" : "outline"} className="h-7 font-mono text-[10px]" onClick={() => onSelect(t)} data-testid={`ml-select-${i}`}>
              {selectedTrackId === t.id ? <><Check className="w-3 h-3 mr-1" />Selected</> : "Select"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
